import logo from "../../assets/images/logo.svg";

/**
 * Shimmer placeholder shown during the post-login bootstrap (while `useMe`
 * is fetching the user/partner). Mimics the buyer home page layout — hero
 * with search bar and a grid of recommended product cards — so the screen
 * doesn't visually "jump" once data arrives.
 *
 * Sellers and admins see a different home, but their bootstrap is fast
 * enough that this skeleton flashes briefly. The buyer layout is the
 * sensible default since buyers are the bulk of the audience.
 */
export function HomePageSkeleton() {
  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-bgWhite">
      {/* Top bar */}
      <div className="flex h-16 w-full shrink-0 items-center justify-between border-b border-borderDefault px-6">
        <img src={logo} alt="Procurtal" className="h-7 w-auto opacity-60" />
        <div className="flex items-center gap-3">
          <SkelLine className="h-3 w-24" />
          <SkelCircle className="size-9" />
        </div>
      </div>

      <div className="flex flex-1 flex-col overflow-auto">
        {/* Hero */}
        <section className="flex flex-col items-center gap-6 px-4 pt-16 pb-12">
          <div className="flex flex-col items-center gap-3">
            <SkelLine className="h-8 w-72 sm:w-[420px]" />
            <SkelLine className="h-8 w-56 sm:w-[340px]" />
            <SkelLine className="h-3 w-64 sm:w-[400px] mt-2" />
          </div>
          <SkelPill className="h-12 w-full max-w-2xl" />
          <div className="flex flex-wrap items-center justify-center gap-2">
            <SkelChip className="w-24" />
            <SkelChip className="w-32" />
            <SkelChip className="w-28" />
            <SkelChip className="w-20" />
          </div>
        </section>

        {/* Recommended grid */}
        <section className="grid w-full grid-cols-1 gap-4 px-6 pb-12 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </section>
      </div>
    </div>
  );
}

function SkelLine({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-bgLight ${className}`} />;
}

function SkelCircle({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-bgLight ${className}`} />;
}

function SkelPill({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse rounded-full bg-bgLight ${className}`} />;
}

function SkelChip({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse h-7 rounded-full bg-bgLight ${className}`} />;
}

function CardSkeleton() {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-4 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]">
      <SkelLine className="h-44 w-full rounded-lg" />
      <div className="flex flex-col gap-2">
        <SkelLine className="h-3.5 w-3/4" />
        <SkelLine className="h-3 w-2/3" />
        <SkelLine className="h-3 w-1/2" />
      </div>
      <div className="flex items-center justify-between pt-1">
        <SkelLine className="h-4 w-20" />
        <SkelLine className="h-3 w-12" />
      </div>
      <SkelPill className="h-9 w-full mt-1" />
    </div>
  );
}

export default HomePageSkeleton;
